import {
  type GuidedPlanningQuote,
  isTraditionalLTCQuote,
} from '@/features/guided-financial-planning/hooks/use-quotes-display';

export interface PremiumBounds {
  min: number;
  max: number;
  defaultValue: number;
  step: number;
}

export const DEFAULT_PREMIUM_BOUNDS: PremiumBounds = {
  min: 50000,
  max: 300000,
  defaultValue: 100000,
  step: 1000,
};

export function snapPremiumToStep(value: number, min: number, max: number, step: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    return Number.isFinite(value) ? Math.max(min, Math.min(max, value)) : min;
  }
  if (!Number.isFinite(step) || step <= 0) {
    return Math.max(min, Math.min(max, value));
  }
  const clamped = Math.max(min, Math.min(max, value));
  const snapped = Math.round(clamped / step) * step;
  return Math.max(min, Math.min(max, snapped));
}

/**
 * Union of carrier investmentMin/investmentMax across adjustable quotes, or null if not derivable.
 */
export function tryPremiumBoundsFromQuotes(
  quotes: GuidedPlanningQuote[] | undefined,
): PremiumBounds | null {
  if (!quotes || quotes.length === 0) {
    return null;
  }

  const adjustablePolicyQuotes = quotes.filter(
    (quote): quote is PolicyQuote => !isTraditionalLTCQuote(quote),
  );

  const investmentMins = adjustablePolicyQuotes
    .map(quote => quote.investmentMin)
    .filter((value): value is number => typeof value === 'number' && value > 0);
  const investmentMaxs = adjustablePolicyQuotes
    .map(quote => quote.investmentMax)
    .filter((value): value is number => typeof value === 'number' && value > 0);

  if (investmentMins.length === 0 || investmentMaxs.length === 0) {
    return null;
  }

  const leastRestrictiveMinimum = Math.min(...investmentMins);
  const leastRestrictiveMaximum = Math.max(...investmentMaxs);
  const step = DEFAULT_PREMIUM_BOUNDS.step;
  const min = Math.ceil(Math.min(leastRestrictiveMinimum, leastRestrictiveMaximum) / step) * step;
  const max = Math.floor(Math.max(leastRestrictiveMinimum, leastRestrictiveMaximum) / step) * step;

  if (max <= min) {
    return null;
  }

  const firstQuotePremium = adjustablePolicyQuotes[0]?.premiumAmount;
  const defaultValueRaw =
    typeof firstQuotePremium === 'number' && firstQuotePremium > 0
      ? Math.max(min, Math.min(max, firstQuotePremium))
      : Math.floor((min + max) / 2);
  const defaultValue = snapPremiumToStep(defaultValueRaw, min, max, step);

  if (min === max) {
    return {
      min,
      max: min + step,
      defaultValue,
      step,
    };
  }

  return {
    min,
    max,
    defaultValue,
    step,
  };
}

export function computePremiumBoundsWithFallback(
  quotes: GuidedPlanningQuote[] | undefined,
): PremiumBounds {
  return tryPremiumBoundsFromQuotes(quotes) ?? DEFAULT_PREMIUM_BOUNDS;
}

export function applyPaymentPreferenceToPremiumBounds(
  bounds: PremiumBounds,
  paymentPreference: PaymentPreference,
): PremiumBounds {
  let baselineDefault = bounds.defaultValue;
  if (paymentPreference === 'upfront') {
    baselineDefault = 100000;
  } else if (paymentPreference === 'balanced') {
    baselineDefault = 10000;
  }

  const clampedDefault = Math.max(bounds.min, Math.min(bounds.max, baselineDefault));
  return {
    ...bounds,
    defaultValue: snapPremiumToStep(clampedDefault, bounds.min, bounds.max, bounds.step),
  };
}
